import { OauthAttemptStatus } from "@coeff/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { QueryCacheKey } from "../api/queryClient";
import { config } from "../config";
import { sentryCapture, useApiContext } from "../utils";

declare const __LOCAL_DEVELOPMENT_NGROK_API_URL__: string;

const SLACK_OAUTH_SCOPES =
  "channels:read chat:write team:read users:read groups:read im:read im:write mpim:write channels:join links:write links:read";

/**
 * Build the Slack OAuth authorization URL
 */
const buildSlackAuthUrl = (oauthAttemptId: string, email: string): string => {
  const redirectUri = `${config.OAUTH_REDIRECT_URL_BASE}/slack`;

  const state = JSON.stringify({
    from: "webapp",
    oauth_attempt_id: oauthAttemptId,
    email: email,
    data_source_type: "slack",
    ...(__LOCAL_DEVELOPMENT_NGROK_API_URL__
      ? {
          coeff_dev_redirect_base_url: __LOCAL_DEVELOPMENT_NGROK_API_URL__,
        }
      : {}),
  });

  const params = new URLSearchParams({
    client_id: config.SLACK_CLIENT_ID,
    redirect_uri: redirectUri,
    scope: SLACK_OAUTH_SCOPES,
    prompt: "consent",
    response_type: "code",
    state: state,
  });

  return `https://slack.com/oauth/v2/authorize?${params.toString()}`;
};

type UseSlackOAuthOptions = {
  userEmail: string;
  enabled?: boolean;
  onSuccess?: () => void;
  onFailure?: (errorMessage: string) => void;
};

type UseSlackOAuthReturn = {
  isAuthorized: boolean | undefined;
  featureEnabled: boolean | undefined;
  isCheckingStatus: boolean;
  isAuthInProgress: boolean;
  startOAuth: () => Promise<void>;
  cancelOAuth: () => void;
  refetchAuthStatus: () => void;
};

/**
 * Hook to handle Slack OAuth flow in webapp.
 *
 * Usage:
 * ```tsx
 * const { isAuthorized, isAuthInProgress, startOAuth } = useSlackOAuth({
 *   userEmail: user.email,
 *   onSuccess: () => console.log("Connected!"),
 *   onFailure: (err) => console.error(err),
 * });
 * ```
 */
export const useSlackOAuth = (options: UseSlackOAuthOptions): UseSlackOAuthReturn => {
  const { userEmail, enabled = true, onSuccess, onFailure } = options;
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();

  const [oauthAttemptId, setOauthAttemptId] = useState<string | null>(null);
  const [isAuthInProgress, setIsAuthInProgress] = useState(false);
  /** Tab opened synchronously on user click; URL set after createOauthAttempt (async-safe). */
  const oauthTabRef = useRef<Window | null>(null);

  // Check if user already has Slack authorized
  const {
    data: authStatusData,
    isLoading: isCheckingStatus,
    refetch: refetchAuthStatus,
  } = useQuery(
    [QueryCacheKey.SLACK_AUTH_STATUS],
    async () => {
      const response = await apiClient.getSlackAuthStatus();
      return response.data;
    },
    {
      enabled,
      retry: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  // Poll for OAuth attempt status
  const { data: oauthAttemptData } = useQuery(
    [QueryCacheKey.OAUTH_ATTEMPT, oauthAttemptId],
    async () => {
      if (!oauthAttemptId) {
        return null;
      }
      const response = await apiClient.getOauthAttempt({ oauth_attempt_id: oauthAttemptId });
      return response.data;
    },
    {
      enabled: Boolean(isAuthInProgress && oauthAttemptId),
      refetchInterval: data =>
        data?.oauth_attempt?.status === OauthAttemptStatus.InProgress ? 2000 : false,
    }
  );

  // Handle OAuth completion
  useEffect(() => {
    if (!oauthAttemptData?.oauth_attempt) {
      return;
    }

    const { status, error_message } = oauthAttemptData.oauth_attempt;

    if (status === OauthAttemptStatus.Success) {
      if (oauthTabRef.current && !oauthTabRef.current.closed) {
        oauthTabRef.current.close();
      }
      oauthTabRef.current = null;
      setIsAuthInProgress(false);
      setOauthAttemptId(null);
      // Invalidate auth status and channel list so reconnect/reauthorize always reloads data.
      queryClient.invalidateQueries([QueryCacheKey.SLACK_AUTH_STATUS]);
      queryClient.invalidateQueries([QueryCacheKey.SLACK_CHANNELS_AND_USERS]);
      onSuccess?.();
    } else if (status === OauthAttemptStatus.Failure) {
      setIsAuthInProgress(false);
      setOauthAttemptId(null);
      onFailure?.(error_message ?? "Slack authorization failed");
    }
  }, [oauthAttemptData, onSuccess, onFailure, queryClient]);

  // Start the OAuth flow (new tab must be opened synchronously with the click — opening only
  // after await is commonly blocked; we open about:blank first then assign Slack URL).
  const startOAuth = useCallback(async () => {
    let tab: Window | null = null;
    try {
      setIsAuthInProgress(true);

      tab = window.open("about:blank", "_blank");
      if (!tab || tab.closed) {
        setIsAuthInProgress(false);
        setOauthAttemptId(null);
        onFailure?.(
          "Could not open a new tab. Allow pop-ups for this site or try again from the address bar."
        );
        return;
      }
      oauthTabRef.current = tab;

      const { data } = await apiClient.createOauthAttempt({
        create_oauth_attempt_request: {
          data_source_type: "slack",
        },
      });

      const attemptId = data.oauth_attempt.oauth_attempt_id;
      setOauthAttemptId(attemptId);

      const authUrl = buildSlackAuthUrl(attemptId, userEmail);
      tab.location.href = authUrl;
    } catch (error) {
      if (tab && !tab.closed) {
        tab.close();
      }
      oauthTabRef.current = null;
      sentryCapture({ error: error as Error });
      setIsAuthInProgress(false);
      setOauthAttemptId(null);
      onFailure?.("Failed to start Slack authorization. Please try again.");
    }
  }, [apiClient, userEmail, onFailure]);

  // Cancel/reset OAuth flow
  const cancelOAuth = useCallback(() => {
    if (oauthTabRef.current && !oauthTabRef.current.closed) {
      oauthTabRef.current.close();
    }
    oauthTabRef.current = null;
    setIsAuthInProgress(false);
    setOauthAttemptId(null);
  }, []);

  return {
    isAuthorized: authStatusData?.is_authorized,
    featureEnabled: authStatusData?.feature_enabled,
    isCheckingStatus,
    isAuthInProgress,
    startOAuth,
    cancelOAuth,
    refetchAuthStatus,
  };
};
